import React from 'react';
import { useMembershipReport } from './useMembershipReport';
import { LoadingContainer } from '../../ui/LoadingContainer/LoadingContainer';
import styles from './MembershipReport.module.css';

export const MembershipReport: React.FC = () => {
  const { membershipData, isLoading, error, fetchReport, reportDate } = useMembershipReport();

  const openMember = (clubMemberId: number | null | undefined) => {
    if (!clubMemberId) return;
    window.open(`/members/${clubMemberId}`, '_blank');
  };

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className="text-center py-4 text-danger">
          <p>{error}</p>
          <button className="btn btn-outline-primary" onClick={() => fetchReport()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!membershipData && !isLoading) {
    return (
      <div className={styles.errorContainer}>
        <div className="text-center py-4 text-muted">
          <p>No membership data available</p>
        </div>
      </div>
    );
  }

  return (
    <LoadingContainer isLoading={isLoading}>
      <div className={styles.reportContainer}>
        {membershipData && (
          <>
            {/* Header */}
            <div className={styles.reportHeader}>
              <div className={styles.clubTitle}>Tiverton Rod and Gun Club</div>
              <div className={styles.reportTitle}>Membership Report: {reportDate}</div>
            </div>

            {/* Summary Statistics */}
            <div className={styles.statsSection}>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Total Active Members</span>
                <span className={styles.statValue}>
                  {membershipData.activeMembersTotal.toLocaleString()}
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Military Leave of Absence</span>
                <span className={styles.statValue}>{membershipData.militaryLeaveTotal}</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Suspended Members</span>
                <span className={styles.statValue}>{membershipData.suspendedMembersTotal}</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Total Members</span>
                <span className={styles.statValue}>
                  {membershipData.membersTotal.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Membership Breakdown */}
            <div className={styles.membershipBreakdown}>
              <div className={styles.breakdownRow}>
                <span className={styles.breakdownNumber}>
                  {membershipData.honoraryMembersTotal}
                </span>
                <span className={styles.breakdownLabel}>Honorary Members</span>
              </div>
              <div className={styles.breakdownRow}>
                <span className={styles.breakdownNumber}>{membershipData.lifeMembersTotal}</span>
                <span className={styles.breakdownLabel}>Life Members</span>
              </div>
              <div className={styles.breakdownRow}>
                <span className={styles.breakdownNumber}>{membershipData.regularMembersTotal}</span>
                <span className={styles.breakdownLabel}>Regular Members</span>
              </div>
              <div className={styles.breakdownRow}>
                <span className={styles.breakdownNumber}>
                  {membershipData.secondaryAdultsHonoraryTotal}
                </span>
                <span className={styles.breakdownLabel}>Secondary Adults (Honorary)</span>
              </div>
              <div className={styles.breakdownRow}>
                <span className={styles.breakdownNumber}>
                  {membershipData.provisionalMembersTotal.toLocaleString()}
                </span>
                <span className={styles.breakdownLabel}>Provisional Members</span>
              </div>
              <div className={`${styles.breakdownRow} ${styles.totalRow}`}>
                <span className={styles.breakdownNumber}>
                  {membershipData.membersTotal.toLocaleString()}
                </span>
                <span className={styles.breakdownLabel}>Total</span>
              </div>
            </div>

            {/* Additional Statistics */}
            <div className={styles.additionalStats}>
              <div className={styles.additionalStatRow}>
                <span className={styles.additionalStatNumber}>
                  {membershipData.newMembersTotal}
                </span>
                <span className={styles.additionalStatLabel}>
                  New Members to be voted on tonight.
                </span>
              </div>
              <div className={styles.additionalStatRow}>
                <span className={styles.additionalStatNumber}>{membershipData.peopleAttended}</span>
                <span className={styles.additionalStatLabel}>
                  People attended the New Member Orientation this month.
                </span>
              </div>
              <div className={styles.additionalStatRow}>
                <span className={styles.additionalStatNumber}>
                  {membershipData.candidatesRegular}
                </span>
                <span className={styles.additionalStatLabel}>
                  Candidates for REGULAR membership to be voted on tonight.
                </span>
              </div>
              <div className={styles.additionalStatRow}>
                <span className={styles.additionalStatNumber}>
                  {membershipData.candidatesSenior}
                </span>
                <span className={styles.additionalStatLabel}>
                  Candidates for SENIOR membership.
                </span>
              </div>
              <div className={styles.additionalStatRow}>
                <span className={styles.additionalStatNumber}>{membershipData.candidatesLife}</span>
                <span className={styles.additionalStatLabel}>Candidates for LIFE membership.</span>
              </div>
            </div>

            {/* Financial Summary Section */}
            {membershipData.financialSummary && (
              <div className={styles.financialSummarySection}>
                <h3 className={styles.financialHeader}>
                  Financial Summary for Billing Year {membershipData.financialSummary.billingYear}{' '}
                  (report period{' '}
                  {new Date(membershipData.financialSummary.reportPeriodStart).toLocaleDateString(
                    'en-US'
                  )}{' '}
                  thru{' '}
                  {new Date(membershipData.financialSummary.reportPeriodEnd).toLocaleDateString(
                    'en-US'
                  )}
                  ):
                </h3>
                <p className={styles.financialSubtitle}>
                  Year to date, there are {membershipData.financialSummary.totalNewMembers} new
                  members for a total of $
                  {membershipData.financialSummary.totalNewMemberPayments.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </p>

                <div className={styles.tableContainer}>
                  <h4 className={styles.tableTitle}>New Members YTD:</h4>
                  <table className={styles.financialTable}>
                    <thead>
                      <tr>
                        <th></th>
                        <th>Members</th>
                        <th>Secondary Adults</th>
                        <th>Total New Members</th>
                        <th>Sum of Payments</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className={styles.rowLabel}>Active:</td>
                        <td>{membershipData.financialSummary.activeNewMembers.members}</td>
                        <td>{membershipData.financialSummary.activeNewMembers.secondaryAdults}</td>
                        <td>{membershipData.financialSummary.activeNewMembers.totalNewMembers}</td>
                        <td>
                          $
                          {membershipData.financialSummary.activeNewMembers.sumOfPayments.toLocaleString(
                            'en-US',
                            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.rowLabel}>Candidates:</td>
                        <td>{membershipData.financialSummary.candidateNewMembers.members}</td>
                        <td>
                          {membershipData.financialSummary.candidateNewMembers.secondaryAdults}
                        </td>
                        <td>
                          {membershipData.financialSummary.candidateNewMembers.totalNewMembers}
                        </td>
                        <td>
                          $
                          {membershipData.financialSummary.candidateNewMembers.sumOfPayments.toLocaleString(
                            'en-US',
                            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                          )}
                        </td>
                      </tr>
                      <tr className={styles.totalRow}>
                        <td className={styles.rowLabel}>Totals:</td>
                        <td>
                          {membershipData.financialSummary.activeNewMembers.members +
                            membershipData.financialSummary.candidateNewMembers.members}
                        </td>
                        <td>
                          {membershipData.financialSummary.activeNewMembers.secondaryAdults +
                            membershipData.financialSummary.candidateNewMembers.secondaryAdults}
                        </td>
                        <td>{membershipData.financialSummary.totalNewMembers}</td>
                        <td>
                          $
                          {membershipData.financialSummary.totalNewMemberPayments.toLocaleString(
                            'en-US',
                            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Work Hours Summary */}
            <div className={styles.workHoursSummary}>
              <p>
                <strong>
                  Total Volunteer Work Hours for {membershipData.previousBillingYear} =
                </strong>{' '}
                {membershipData.totalWorkHoursLastYear.toLocaleString()}
              </p>
              <p>
                <strong>
                  Total Volunteer Work Hours for {membershipData.currentBillingYear} =
                </strong>{' '}
                {membershipData.totalWorkHoursThisYear.toLocaleString()}
              </p>
            </div>

            {/* Member Lists Section */}
            <div className={styles.memberListsSection}>
              <h2 className={styles.memberListsHeader}>
                New Members and Membership Changes: {reportDate}
              </h2>

              {/* Family Membership Lists - Full Width Layout */}
              <div className={styles.familyMembershipsList}>
                {/* Family Memberships */}
                <div className={styles.familyMemberCard}>
                  <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>Family Memberships</h3>
                    <span className={styles.cardCount}>
                      {membershipData.familyMemberhips?.length || 0}
                    </span>
                  </div>
                  <div className={styles.cardContent}>
                    {membershipData.familyMemberhips &&
                    membershipData.familyMemberhips.length > 0 ? (
                      membershipData.familyMemberhips.map((member, index) => (
                        <div key={index} className={styles.familyNameItem}>
                          <span className={styles.nameNumber}>{index + 1}.</span>
                          <span className={styles.nameMember}>
                            <span
                              className={styles.clickableName}
                              onClick={() => openMember(member.clubMemberId)}>
                              {member.memberName}
                            </span>
                            {member.secondaryMemberName && (
                              <span className={styles.secondaryMember}>
                                {' - '}
                                <span
                                  className={styles.clickableName}
                                  onClick={() => openMember(member.clubMemberId)}>
                                  {member.secondaryMemberName}
                                </span>
                              </span>
                            )}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className={styles.emptyState}>No members in this category</div>
                    )}
                  </div>
                </div>

                {/* Military Family Memberships */}
                <div className={styles.familyMemberCard}>
                  <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>Military Family Memberships</h3>
                    <span className={styles.cardCount}>
                      {membershipData.militaryFamilyMemberships?.length || 0}
                    </span>
                  </div>
                  <div className={styles.cardContent}>
                    {membershipData.militaryFamilyMemberships &&
                    membershipData.militaryFamilyMemberships.length > 0 ? (
                      membershipData.militaryFamilyMemberships.map((member, index) => (
                        <div key={index} className={styles.familyNameItem}>
                          <span className={styles.nameNumber}>{index + 1}.</span>
                          <span className={styles.nameMember}>
                            <span
                              className={styles.clickableName}
                              onClick={() => openMember(member.clubMemberId)}>
                              {member.memberName}
                            </span>
                            {member.secondaryMemberName && (
                              <span className={styles.secondaryMember}>
                                {' - '}
                                <span
                                  className={styles.clickableName}
                                  onClick={() => openMember(member.clubMemberId)}>
                                  {member.secondaryMemberName}
                                </span>
                              </span>
                            )}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className={styles.emptyState}>No members in this category</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Non-Family Membership Lists - Card Layout */}
              <div className={styles.simpleListsGrid}>
                {/* Military Memberships */}
                <div className={styles.memberCard}>
                  <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>Military Memberships</h3>
                    <span className={styles.cardCount}>
                      {membershipData.militaryMemberships?.length || 0}
                    </span>
                  </div>
                  <div className={styles.cardContent}>
                    {membershipData.militaryMemberships &&
                    membershipData.militaryMemberships.length > 0 ? (
                      membershipData.militaryMemberships.map((member, index) => (
                        <div key={index} className={styles.nameItem}>
                          <span className={styles.nameNumber}>{index + 1}.</span>
                          <span
                            className={`${styles.nameMember} ${styles.clickableName}`}
                            onClick={() => openMember(member.clubMemberId)}>
                            {member.memberName}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className={styles.emptyState}>No members in this category</div>
                    )}
                  </div>
                </div>

                {/* Standard Memberships */}
                <div className={styles.memberCard}>
                  <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>Standard Memberships</h3>
                    <span className={styles.cardCount}>
                      {membershipData.standardMembershps?.length || 0}
                    </span>
                  </div>
                  <div className={styles.cardContent}>
                    {membershipData.standardMembershps &&
                    membershipData.standardMembershps.length > 0 ? (
                      membershipData.standardMembershps.map((member, index) => (
                        <div key={index} className={styles.nameItem}>
                          <span className={styles.nameNumber}>{index + 1}.</span>
                          <span
                            className={`${styles.nameMember} ${styles.clickableName}`}
                            onClick={() => openMember(member.clubMemberId)}>
                            {member.memberName}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className={styles.emptyState}>No members in this category</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Candidates Tables */}
              <div className={styles.candidatesSection}>
                {/* Candidates for Regular Membership */}
                <div className={styles.candidateTable}>
                  <div className={styles.tableHeader}>
                    <h3 className={styles.tableTitle}>
                      Candidates for Regular Membership
                      <span className={styles.tableSubtitle}>
                        [1+ years and 15 work hours (23 hours for Family)]
                      </span>
                    </h3>
                    <span className={styles.tableCount}>
                      {membershipData.regularMembersCandidates?.length || 0}
                    </span>
                  </div>
                  <div className={styles.tableWrapper}>
                    {membershipData.regularMembersCandidates &&
                    membershipData.regularMembersCandidates.length > 0 ? (
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th className={styles.thNumber}>#</th>
                            <th className={styles.thName}>Member Name</th>
                            <th className={styles.thDate}>Joined</th>
                            <th className={styles.thYears}>Time Since Joined</th>
                            <th className={styles.thType}>Type</th>
                            <th className={styles.thHours}>Work Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {membershipData.regularMembersCandidates.map((member, index) => (
                            <tr
                              key={index}
                              className={styles.clickableRow}
                              onClick={() => openMember(member.clubMemberId)}>
                              <td className={styles.tdNumber}>{index + 1}</td>
                              <td className={styles.tdName}>{member.formattedMemberName}</td>
                              <td className={styles.tdDate}>{member.formattedJoinedDate}</td>
                              <td className={styles.tdYears}>{member.formattedTimeSinceJoined}</td>
                              <td className={styles.tdType}>{member.membershipType}</td>
                              <td className={styles.tdHours}>{member.workHours}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className={styles.emptyTableState}>No candidates in this category</div>
                    )}
                  </div>
                </div>

                {/* Candidates for Senior Membership */}
                <div className={styles.candidateTable}>
                  <div className={styles.tableHeader}>
                    <h3 className={styles.tableTitle}>
                      Candidates for Senior Membership
                      <span className={styles.tableSubtitle}>
                        [Age 62+ and 10+ years of membership]
                      </span>
                    </h3>
                    <span className={styles.tableCount}>
                      {membershipData.seniorMemberCandidates?.length || 0}
                    </span>
                  </div>
                  <div className={styles.tableWrapper}>
                    {membershipData.seniorMemberCandidates &&
                    membershipData.seniorMemberCandidates.length > 0 ? (
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th className={styles.thNumber}>#</th>
                            <th className={styles.thName}>Member Name</th>
                            <th className={styles.thDate}>Date of Birth</th>
                            <th className={styles.thAge}>Age</th>
                            <th className={styles.thYears}>Years of Membership</th>
                          </tr>
                        </thead>
                        <tbody>
                          {membershipData.seniorMemberCandidates.map((member, index) => (
                            <tr
                              key={index}
                              className={styles.clickableRow}
                              onClick={() => openMember(member.clubMemberId)}>
                              <td className={styles.tdNumber}>{index + 1}</td>
                              <td className={styles.tdName}>{member.formattedMemberName}</td>
                              <td className={styles.tdDate}>{member.formattedDateOfBirth}</td>
                              <td className={styles.tdAge}>{member.formattedAge}</td>
                              <td className={styles.tdYears}>{member.formattedYearsFirstJoined}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className={styles.emptyTableState}>No candidates in this category</div>
                    )}
                  </div>
                </div>

                {/* Candidates for Life Membership */}
                <div className={styles.candidateTable}>
                  <div className={styles.tableHeader}>
                    <h3 className={styles.tableTitle}>
                      Candidates for Life Membership
                      <span className={styles.tableSubtitle}>
                        [Age 65+ and 30+ years of membership]
                      </span>
                    </h3>
                    <span className={styles.tableCount}>
                      {membershipData.lifeMemberCandidates?.length || 0}
                    </span>
                  </div>
                  <div className={styles.tableWrapper}>
                    {membershipData.lifeMemberCandidates &&
                    membershipData.lifeMemberCandidates.length > 0 ? (
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th className={styles.thNumber}>#</th>
                            <th className={styles.thName}>Member Name</th>
                            <th className={styles.thDate}>Date of Birth</th>
                            <th className={styles.thAge}>Age</th>
                            <th className={styles.thYears}>Years of Membership</th>
                          </tr>
                        </thead>
                        <tbody>
                          {membershipData.lifeMemberCandidates.map((member, index) => (
                            <tr
                              key={index}
                              className={styles.clickableRow}
                              onClick={() => openMember(member.clubMemberId)}>
                              <td className={styles.tdNumber}>{index + 1}</td>
                              <td className={styles.tdName}>{member.formattedMemberName}</td>
                              <td className={styles.tdDate}>{member.formattedDateOfBirth}</td>
                              <td className={styles.tdAge}>{member.formattedAge}</td>
                              <td className={styles.tdYears}>{member.formattedYearsFirstJoined}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className={styles.emptyTableState}>No candidates in this category</div>
                    )}
                  </div>
                </div>

                {/* Children turning 18 years old */}
                <div className={styles.candidateTable}>
                  <div className={styles.tableHeader}>
                    <h3 className={styles.tableTitle}>Children Turning 18 Years Old</h3>
                    <span className={styles.tableCount}>
                      {membershipData.childrenTurningEighteen?.length || 0}
                    </span>
                  </div>
                  <div className={styles.tableWrapper}>
                    {membershipData.childrenTurningEighteen &&
                    membershipData.childrenTurningEighteen.length > 0 ? (
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th className={styles.thNumber}>#</th>
                            <th className={styles.thName}>Child Name</th>
                            <th className={styles.thDate}>Birth Date</th>
                            <th className={styles.thAge}>Age</th>
                            <th className={styles.thParent}>Parent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {membershipData.childrenTurningEighteen.map((child, index) => (
                            <tr key={index}>
                              <td className={styles.tdNumber}>{index + 1}</td>
                              <td className={styles.tdName}>
                                <span
                                  className={styles.clickableName}
                                  onClick={() => openMember(child.childClubMemberId)}>
                                  {child.formattedChildName}
                                </span>
                              </td>
                              <td className={styles.tdDate}>{child.formattedBirthDate}</td>
                              <td className={styles.tdAge}>{child.age}</td>
                              <td className={styles.tdParent}>
                                <span
                                  className={styles.clickableName}
                                  onClick={() => openMember(child.parentClubMemberId)}>
                                  {child.formattedParentInfo}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className={styles.emptyTableState}>No children in this category</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Print Button */}
            <div className={styles.printButtonContainer}>
              <button className={styles.printButton} onClick={() => window.print()} type="button">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className={styles.printIcon}>
                  <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" />
                  <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2H5zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1z" />
                </svg>
                Print Report
              </button>
            </div>
          </>
        )}
      </div>
    </LoadingContainer>
  );
};
